import React, { useEffect, useState, useCallback } from "react";
import { Card, Button, Spinner, Alert, Row, Col, Badge, Table, Nav, Form, Modal } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { 
  Plus, 
  Calendar, 
  Clock, 
  IndianRupee, 
  Users, 
  CheckCircle2, 
  BookOpen,
  GraduationCap,
  Monitor,
  Play,
  MoreVertical,
  Sparkles,
  Layers,
  FileText,
  Settings,
  ExternalLink,
  Download,
  Upload,
  Eye,
  Pencil
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import { formatToCurrency } from "../../utils/common";
import MaterialCard from "../Materials/MaterialCard";
import UploadMaterialModal from "../Materials/UploadMaterialModal";
import CommonToaster from "../Common/CommonToaster";
import { ConfirmModal, AppButton, BackButton } from "../ui";
import DOMPurify from "dompurify";
import "./CourseProfile.scss";

const keyHighlights = [
  "Comprehensive syllabus",
  "Interactive learning sessions",
  "Personalized mentoring",
  "Assessment & feedback",
];

const CourseProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [course, setCourse] = useState(null);
  const [batches, setBatches] = useState([]);
  const [syllabus, setSyllabus] = useState(null);
  const [activeTab, setActiveTab] = useState("batches");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Course materials state
  const [courseMaterials, setCourseMaterials] = useState([]);
  const [materialsLoading, setMaterialsLoading] = useState(false);
  const [showMaterialUpload, setShowMaterialUpload] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [editMaterial, setEditMaterial] = useState(null);
  const [showEditMaterial, setShowEditMaterial] = useState(false);
  const [editMaterialForm, setEditMaterialForm] = useState({ title: "", description: "", topic: "", category: "other", isPreview: false });
  const [editSaving, setEditSaving] = useState(false);
  const [deleteMaterial, setDeleteMaterial] = useState(null);
  const [playingMaterial, setPlayingMaterial] = useState(null);
  const [streamUrl, setStreamUrl] = useState(null);
  const [streamLoading, setStreamLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setErr("");
      try {
        const [courseRes, batchRes, syllabusRes] = await Promise.all([
          apiClient.get(`/courses/${id}`),
          apiClient.get("/batches/list?pageSize=1000&page=1", { params: { course: id } }),
          apiClient.get(`/syllabus/template/${id}`).catch(() => null),
        ]);
        setCourse(courseRes.data.course);
        setBatches(batchRes.data.batches || []);
        if (syllabusRes?.data?.template) {
          setSyllabus(syllabusRes.data.template);
        }
      } catch (e) {
        setErr("Failed to load course details.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  // Fetch course-level materials
  const fetchCourseMaterials = useCallback(async () => {
    setMaterialsLoading(true);
    try {
      const { data } = await apiClient.get(`/materials/course/${id}`, { params: { page: 1, limit: 100 } });
      if (data.success) setCourseMaterials(data.data || []);
    } catch {
      setCourseMaterials([]);
    }
    setMaterialsLoading(false);
  }, [id]);

  useEffect(() => {
    if (activeTab === "materials") fetchCourseMaterials();
  }, [activeTab, fetchCourseMaterials]);

  // Material handlers
  const handleMaterialDownload = async (material) => {
    try {
      const { data } = await apiClient.get(`/materials/${material._id}/download`);
      if (data.success) window.open(data.data.url, "_blank");
    } catch {
      setToast({ show: true, message: "Failed to download.", type: "error" });
    }
  };

  const handleMaterialEditOpen = (material) => {
    setEditMaterial(material);
    setEditMaterialForm({
      title: material.title || "",
      description: material.description || "",
      topic: material.topic || "",
      category: material.category || "other",
      isPreview: material.isPreview || false,
    });
    setShowEditMaterial(true);
  };

  const handleMaterialEditSave = async () => {
    if (!editMaterialForm.title.trim()) return;
    setEditSaving(true);
    try {
      const body = {
        title: editMaterialForm.title.trim(),
        description: editMaterialForm.description.trim(),
        topic: editMaterialForm.topic.trim(),
        category: editMaterialForm.category,
      };
      if (editMaterial?.isVideo) {
        body.isPreview = editMaterialForm.isPreview;
      }
      const { data } = await apiClient.put(`/materials/${editMaterial._id}`, body);
      if (data.success) {
        setToast({ show: true, message: "Material updated.", type: "success" });
        setShowEditMaterial(false);
        fetchCourseMaterials();
      }
    } catch {
      setToast({ show: true, message: "Failed to update.", type: "error" });
    }
    setEditSaving(false);
  };

  const handleMaterialDelete = (material) => setDeleteMaterial(material);

  const handleMaterialPlay = async (material) => {
    setPlayingMaterial(material);
    setStreamUrl(null);
    setStreamLoading(true);
    try {
      const { data } = await apiClient.get(`/materials/${material._id}/stream`);
      if (data.success) setStreamUrl(data.data.url);
    } catch {
      setToast({ show: true, message: "Failed to load video.", type: "error" });
      setPlayingMaterial(null);
    }
    setStreamLoading(false);
  };

  const confirmMaterialDelete = async () => {
    const material = deleteMaterial;
    setDeleteMaterial(null);
    try {
      const { data } = await apiClient.delete(`/materials/${material._id}`);
      if (data.success) {
        setToast({ show: true, message: "Material deleted.", type: "success" });
        fetchCourseMaterials();
      }
    } catch {
      setToast({ show: true, message: "Failed to delete.", type: "error" });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      timeZone: "Asia/Kolkata",
    });
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getStatusVariant = (status) => {
    const statusLower = status?.toLowerCase();
    if (statusLower === "active") return "success";
    if (statusLower === "upcoming") return "warning";
    if (statusLower === "completed") return "primary";
    return "secondary";
  };

  if (loading)
    return (
      <div className="text-center my-5">
        <Spinner animation="border" variant="secondary" />
      </div>
    );

  if (err)
    return (
      <Alert variant="danger" className="my-5 text-center">
        {err}
      </Alert>
    );

  if (!course)
    return <div className="text-muted text-center my-5">Course not found.</div>;

  const isHtml = (s) => typeof s === 'string' && /<[a-z][\s\S]*>/i.test(s);

  // Parse highlights: extract text from HTML <li> tags, or split by comma, or use defaults
  const parseHighlights = (raw) => {
    if (!raw) return keyHighlights;
    if (isHtml(raw)) {
      const tmp = document.createElement('div');
      tmp.innerHTML = DOMPurify.sanitize(raw);
      const items = tmp.querySelectorAll('li');
      if (items.length > 0) return Array.from(items).map(li => li.textContent.trim()).filter(Boolean);
      // Fallback: strip tags and split by newline
      const text = tmp.textContent || '';
      const lines = text.split('\n').map(s => s.trim()).filter(Boolean);
      return lines.length > 0 ? lines : keyHighlights;
    }
    return raw.split(',').map(h => h.trim()).filter(Boolean);
  };
  const highlights = parseHighlights(course.highlights);

  // Get the latest batch start date
  const latestBatchStartDate = batches.length > 0
    ? batches.reduce((latest, batch) => {
        if (!batch.startDate) return latest;
        const batchDate = new Date(batch.startDate);
        return !latest || batchDate > new Date(latest) ? batch.startDate : latest;
      }, null)
    : course.currentBatch;

  return (
    <div className="course-details-wrapper">
      {/* Back Button and Header */}
      <div className="course-back-button">
        <BackButton onClick={() => navigate("/courses")} />
        <div className="page-header-section">
          <div>
            <h1>Course Details</h1>
            <p>Explore the information and batch schedule for this course</p>
          </div>
        </div>
        <AppButton size="sm" onClick={() => navigate(`/courses/edit/${id}`)} style={{ marginLeft: 'auto' }}>
          <Pencil size={15} /> Edit Course
        </AppButton>
      </div>

      {/* Course Header Card */}
      <Card className="course-header-card">
        <div className="course-header-gradient">
          <Row>
            <Col lg={9} className="mb-4 mb-lg-0">
              {/* Course Title Section */}
              <div className="course-title-section">
                <div className="course-icon-large">
                  <BookOpen size={32} className="text-primary" />
                </div>
                <div className="flex-grow-1">
                  <div className="d-flex align-items-center gap-3 mb-1">
                    <h2 className="course-title">{course.name}</h2>
                    <Badge className="course-enrolled-badge">
                      {course.studentCount || 0} Students enrolled
                    </Badge>
                  </div>
                  <p className="course-code">{course.title}</p>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="course-stats-grid">
                <div className="course-stat-box stat-blue">
                  <div className="stat-icon-wrapper">
                    <Calendar size={18} />
                  </div>
                  <div className="flex-grow-1">
                    <p className="stat-label mb-0">Start Date</p>
                    <p className="stat-value mb-0">{formatDate(latestBatchStartDate)}</p>
                  </div>
                </div>

                <div className="course-stat-box stat-violet">
                  <div className="stat-icon-wrapper">
                    <Clock size={18} />
                  </div>
                  <div className="flex-grow-1">
                    <p className="stat-label mb-0">Duration</p>
                    <p className="stat-value mb-0">{course.duration || "3 Months"}</p>
                  </div>
                </div>

                <div className="course-stat-box stat-emerald">
                  <div className="stat-icon-wrapper">
                    <IndianRupee size={18} />
                  </div>
                  <div className="flex-grow-1">
                    <p className="stat-label mb-0">Fees</p>
                    <p className="stat-value mb-0">{formatToCurrency(course.fees) || "N/A"}</p>
                  </div>
                </div>

                <div className="course-stat-box stat-amber">
                  <div className="stat-icon-wrapper">
                    <Play size={18} />
                  </div>
                  <div className="flex-grow-1">
                    <p className="stat-label mb-0">Free Demo</p>
                    <p className="stat-value mb-0">{course.freeDemo || "No"}</p>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="course-description-section">
                <h4>Course Description</h4>
                {isHtml(course.description) ? (
                  <div className="ql-editor" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(course.description) }} />
                ) : (
                  <p>{course.description || "No description available."}</p>
                )}
              </div>

              {/* Technologies */}
              {course.technologies && (
                <div className="course-tech-badges">
                  {course.technologies.split(",").map((tech, index) => (
                    <Badge key={index} className="tech-badge">
                      {tech.trim()}
                    </Badge>
                  ))}
                </div>
              )}
            </Col>

            {/* Key Highlights Sidebar */}
            <Col lg={3}>
              <Card className="course-highlights-card">
                <Card.Header className="border-bottom-0">
                  <Card.Title>
                    <Sparkles size={20} className="text-warning" />
                    Key Highlights
                  </Card.Title>
                </Card.Header>
                <Card.Body>
                  <div className="highlights-list">
                    {highlights.map((highlight, index) => (
                      <div key={index} className="highlight-item">
                        <CheckCircle2 size={20} className="check-icon" />
                        <span>{highlight}</span>
                      </div>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>
      </Card>

      {/* Tabs */}
      <Card className="course-tabs-card">
        <Nav className="course-detail-tabs" activeKey={activeTab} onSelect={setActiveTab}>
          <Nav.Item>
            <Nav.Link eventKey="batches" className="course-tab-link">
              <Layers size={15} />
              Batch List
              <Badge className="tab-count-badge">{batches.length}</Badge>
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="syllabus" className="course-tab-link">
              <BookOpen size={15} />
              Syllabus
              {syllabus && <Badge className="tab-active-badge">v{syllabus.version}</Badge>}
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="materials" className="course-tab-link">
              <FileText size={15} />
              Materials
            </Nav.Link>
          </Nav.Item>
        </Nav>

        {/* ── Batch List Tab ── */}
        {activeTab === "batches" && (
          <>
            <Card.Header className="tab-content-header">
              <Card.Title>Batches List</Card.Title>
              <Button
                variant="primary"
                onClick={() => navigate(`/batches/add?course=${id}`)}
              >
                <Plus size={16} className="me-2" />
                Add New Batch
              </Button>
            </Card.Header>
            <Card.Body className="p-0">
              {!batches || batches.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">
                    <Users size={32} />
                  </div>
                  <h3>No batches available</h3>
                  <p>Create a new batch to get started</p>
                  <Button
                    variant="primary"
                    onClick={() => navigate(`/batches/add?course=${id}`)}
                  >
                    <Plus size={16} className="me-2" />
                    Add New Batch
                  </Button>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table className="data-table mb-0">
                    <thead>
                      <tr>
                        <th>Batch No</th>
                        <th>Instructor</th>
                        <th>Status</th>
                        <th>Start Date</th>
                        <th>End Date</th>
                        <th>Time</th>
                        <th className="text-center">Students</th>
                        <th>Mode</th>
                        <th style={{ width: 50 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {batches.map((batch, index) => (
                        <tr key={batch._id}>
                          <td>
                            <span className="fw-semibold">{batch.name || `Batch ${index + 1}`}</span>
                          </td>
                          <td>
                            <div className="batch-instructor-cell">
                              <div className="instructor-avatar">
                                {getInitials(batch.instructor)}
                              </div>
                              <span>{batch.instructor || "N/A"}</span>
                            </div>
                          </td>
                          <td>
                            <Badge bg={getStatusVariant(batch.status)}>
                              {batch.status || "Active"}
                            </Badge>
                          </td>
                          <td>{formatDate(batch.startDate)}</td>
                          <td>{formatDate(batch.endDate)}</td>
                          <td>
                            <span className="batch-time-badge">
                              {batch.startTime && batch.endTime 
                                ? `${batch.startTime} - ${batch.endTime}` 
                                : batch.startTime || "N/A"}
                            </span>
                          </td>
                          <td className="text-center">
                            <Badge className="batch-students-badge">
                              {batch.studentCount || 0}
                            </Badge>
                          </td>
                          <td>
                            <div
                              className={`batch-mode-indicator ${
                                batch.mode === "Offline"
                                  ? "mode-offline"
                                  : batch.mode === "Both"
                                  ? "mode-both"
                                  : "mode-online"
                              }`}
                            >
                              <Monitor size={16} />
                              <span>{batch.mode || "Online"}</span>
                            </div>
                          </td>
                          <td>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="p-1"
                              onClick={() => navigate(`/batches/${batch._id}`)}
                            >
                              <MoreVertical size={16} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </>
        )}

        {/* ── Syllabus Tab ── */}
        {activeTab === "syllabus" && (
          <>
            <Card.Header className="tab-content-header">
              <div className="d-flex align-items-center gap-2">
                <Card.Title className="mb-0">Course Syllabus</Card.Title>
                {syllabus && (
                  <Badge className="syllabus-active-version-badge">
                    Active v{syllabus.version}
                  </Badge>
                )}
              </div>
              <Button
                variant="primary"
                onClick={() => navigate(`/courses/${id}/syllabus`)}
              >
                <Settings size={16} className="me-2" />
                Manage Syllabus
              </Button>
            </Card.Header>
            <Card.Body>
              {!syllabus ? (
                <div className="empty-state">
                  <div className="empty-icon">
                    <BookOpen size={32} />
                  </div>
                  <h3>No syllabus created yet</h3>
                  <p>Create a day-wise syllabus template for this course</p>
                  <Button
                    variant="primary"
                    onClick={() => navigate(`/courses/${id}/syllabus`)}
                  >
                    <Plus size={16} className="me-2" />
                    Create Syllabus
                  </Button>
                </div>
              ) : (
                <div className="syllabus-preview">
                  {/* Syllabus Stats */}
                  <div className="syllabus-preview-stats">
                    <div className="sp-stat">
                      <Layers size={16} className="sp-stat-icon" />
                      <div>
                        <span className="sp-stat-value">{syllabus.days.length}</span>
                        <span className="sp-stat-label">Days</span>
                      </div>
                    </div>
                    <div className="sp-stat">
                      <FileText size={16} className="sp-stat-icon" />
                      <div>
                        <span className="sp-stat-value">
                          {syllabus.days.reduce((sum, d) => sum + (d.topics?.length || 0), 0)}
                        </span>
                        <span className="sp-stat-label">Topics</span>
                      </div>
                    </div>
                    <div className="sp-stat">
                      <Clock size={16} className="sp-stat-icon" />
                      <div>
                        <span className="sp-stat-value">
                          {Math.round(syllabus.days.reduce((sum, d) => sum + (d.estimatedDuration || 0), 0) / 60)}h
                        </span>
                        <span className="sp-stat-label">Total Duration</span>
                      </div>
                    </div>
                  </div>

                  {/* Day-wise list */}
                  <div className="syllabus-day-list">
                    {syllabus.days.map((day) => (
                      <div key={day.dayNumber} className="syllabus-day-row">
                        <div className="sdr-left">
                          <span className="sdr-day-badge">Day {day.dayNumber}</span>
                          <div className="sdr-info">
                            <span className="sdr-title">{day.title}</span>
                            <span className="sdr-meta">
                              {day.topics?.length || 0} topics • {day.estimatedDuration || 0} min
                            </span>
                          </div>
                        </div>
                        <div className="sdr-topics">
                          {day.topics?.map((topic, i) => (
                            <span key={i} className="sdr-topic-chip">{topic}</span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Manage link */}
                  <div className="syllabus-preview-footer">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => navigate(`/courses/${id}/syllabus`)}
                    >
                      <ExternalLink size={14} className="me-1" />
                      Edit Syllabus
                    </Button>
                  </div>
                </div>
              )}
            </Card.Body>
          </>
        )}

        {/* ── Materials Tab ── */}
        {activeTab === "materials" && (
          <>
            <Card.Header className="tab-content-header">
              <Card.Title>Course Materials</Card.Title>
              <Button variant="primary" onClick={() => setShowMaterialUpload(true)}>
                <Upload size={16} className="me-2" />
                Upload Material
              </Button>
            </Card.Header>
            <Card.Body>
              {materialsLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" variant="secondary" />
                </div>
              ) : courseMaterials.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">
                    <FileText size={32} />
                  </div>
                  <h3>No course materials yet</h3>
                  <p>Upload shared materials that will be available across all batches of this course.</p>
                  <Button variant="primary" onClick={() => setShowMaterialUpload(true)}>
                    <Plus size={16} className="me-2" />
                    Upload Material
                  </Button>
                </div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "12px" }}>
                  {courseMaterials.map((m) => (
                    <MaterialCard
                      key={m._id}
                      material={m}
                      role="admin"
                      onDownload={handleMaterialDownload}
                      onEdit={handleMaterialEditOpen}
                      onDelete={handleMaterialDelete}
                      onPlay={handleMaterialPlay}
                    />
                  ))}
                </div>
              )}
            </Card.Body>
          </>
        )}
      </Card>

      {/* Course-level Material Upload Modal */}
      <UploadMaterialModal
        show={showMaterialUpload}
        onClose={() => setShowMaterialUpload(false)}
        courseId={id}
        defaultLevel="course"
        hideLevel
        onSuccess={() => {
          setShowMaterialUpload(false);
          setToast({ show: true, message: "Material uploaded!", type: "success" });
          fetchCourseMaterials();
        }}
      />

      {/* Edit Material Modal */}
      <Modal
        show={showEditMaterial}
        onHide={() => !editSaving && setShowEditMaterial(false)}
        centered
        className="upload-material-modal"
      >
        <Modal.Header closeButton={!editSaving}>
          <Modal.Title className="fs-6 fw-semibold">Edit Material</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="small fw-medium">Title</Form.Label>
            <Form.Control
              type="text"
              value={editMaterialForm.title}
              onChange={(e) => setEditMaterialForm((f) => ({ ...f, title: e.target.value }))}
              disabled={editSaving}
              size="sm"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="small fw-medium">Category</Form.Label>
            <Form.Select
              value={editMaterialForm.category}
              onChange={(e) => setEditMaterialForm((f) => ({ ...f, category: e.target.value }))}
              disabled={editSaving}
              size="sm"
            >
              <option value="ppt">PPT / Presentation</option>
              <option value="script">Script</option>
              <option value="notes">Notes</option>
              <option value="other">Other</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="small fw-medium">Topic</Form.Label>
            <Form.Control
              type="text"
              value={editMaterialForm.topic}
              onChange={(e) => setEditMaterialForm((f) => ({ ...f, topic: e.target.value }))}
              disabled={editSaving}
              size="sm"
            />
          </Form.Group>
          <Form.Group>
            <Form.Label className="small fw-medium">Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={editMaterialForm.description}
              onChange={(e) => setEditMaterialForm((f) => ({ ...f, description: e.target.value }))}
              disabled={editSaving}
              size="sm"
            />
          </Form.Group>
          {editMaterial?.isVideo && (
            <div className="material-preview-toggle mt-3">
              <Form.Check
                type="switch"
                id="edit-free-preview-toggle"
                label={
                  <span className="small fw-medium">
                    <Eye size={14} className="me-1" />
                    Free Preview
                  </span>
                }
                checked={editMaterialForm.isPreview}
                onChange={(e) => setEditMaterialForm((f) => ({ ...f, isPreview: e.target.checked }))}
                disabled={editSaving}
              />
              <small className="text-muted d-block mt-1">
                Allow unenrolled students to watch this video before purchasing
              </small>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={() => setShowEditMaterial(false)} disabled={editSaving}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={handleMaterialEditSave} disabled={editSaving || !editMaterialForm.title.trim()}>
            {editSaving ? <Spinner animation="border" size="sm" /> : "Save Changes"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Toaster */}
      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />

      <ConfirmModal
        show={!!deleteMaterial}
        onHide={() => setDeleteMaterial(null)}
        onConfirm={confirmMaterialDelete}
        title="Delete Material"
        message={`Delete "${deleteMaterial?.title}"?`}
        confirmText="Delete"
        variant="danger"
      />

      {/* Video Player Modal */}
      <Modal
        show={!!playingMaterial}
        onHide={() => { setPlayingMaterial(null); setStreamUrl(null); }}
        centered
        size="lg"
        className="upload-material-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title className="fs-6 fw-semibold">{playingMaterial?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {streamLoading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="secondary" /></div>
          ) : streamUrl ? (
            <video src={streamUrl} controls autoPlay style={{ width: '100%', maxHeight: '70vh', display: 'block', background: '#000' }} />
          ) : null}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default CourseProfile;
